<template>
  <!-- Below the large-desktop breakpoint the sidebar is always a drawer, opened from the page header. -->
  <!-- The children animate, so the Transition is told how long they take. -->
  <Transition
    v-if="!isLargeDesktop"
    :duration="FOCUS_DRAWER_DURATION"
    name="focus-drawer"
  >
    <div
      v-if="menuStore.isMobileMenuOpen"
      class="fixed inset-0 z-50 flex"
    >
      <div
        class="focus-drawer-backdrop absolute inset-0 bg-surface-900/30"
        @click="menuStore.closeMobileMenu()"
      />
      <aside
        ref="overlayRef"
        class="focus-drawer-panel relative h-full w-[300px] max-w-[85vw] bg-ground shadow-xl"
      >
        <TwnFocusSidebarPanel
          is-drawer
          @navigate="menuStore.closeMobileMenu()"
        />
      </aside>
    </div>
  </Transition>

  <!-- One width for both shapes, so the page beside it narrows and widens with the fold instead of jumping. -->
  <div
    v-else
    :class="shellClass"
    class="h-full shrink-0 transition-[width] duration-[240ms] ease-[var(--ease-in-out)] motion-reduce:transition-none"
    @transitionend.self="isFolding = false"
  >
    <!-- Folded: a 64px rail. A space icon opens its pages in a panel over the page. -->
    <div
      v-if="mode === 'folded'"
      ref="railRef"
      :class="{ 'focus-entrance': isEntering, 'is-sweeping': isSweeping }"
      class="relative flex h-full w-[64px] shrink-0 flex-col items-center gap-1 py-3.5"
    >
      <Link
        aria-label="Home"
        class="focus-row flex h-8 items-center rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        :href="homePath"
      >
        <LightLogoMark class="block h-5 w-auto dark:hidden" />
        <PinkLogoMark class="hidden h-5 w-auto dark:block" />
      </Link>
      <TwnBaseBtn
        v-tooltip.right="'Open the sidebar ([)'"
        aria-label="Open the sidebar"
        class="focus-row mb-1.5"
        style="--row: 1"
        icon="left_panel_open"
        severity="secondary"
        size="small"
        variant="text"
        @click="setMode('docked')"
      />
      <TwnBaseBtn
        v-tooltip.right="searchTooltip"
        aria-label="Search"
        class="focus-row"
        icon="search"
        style="--row: 2"
        severity="secondary"
        variant="text"
        @click="openCommandPalette"
      />
      <!-- The POS rail's item: the selected one sits on a primary tint with its icon filled. -->
      <button
        v-for="(section, index) in spaces"
        :key="section.key"
        v-tooltip.right="section.name"
        :aria-expanded="isFlyoutExpanded(section)"
        :aria-label="section.name"
        :class="
          isRailActive(section)
            ? 'bg-selected text-selected-foreground'
            : 'text-surface-500 hover:bg-surface-200/60 hover:text-surface-900'
        "
        :style="{ '--row': index + 3 }"
        class="focus-row grid size-[40px] shrink-0 place-items-center rounded-xl transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
        type="button"
        @click="toggleFlyout(section)"
      >
        <MaterialIcon
          :is-filled="isRailActive(section)"
          :ligature="section.icon"
          size="1.25rem"
        />
      </button>
      <span class="flex-1" />
      <!-- The foot lands last, as one block: its two links render more than one root, so they take no class. -->
      <div
        class="focus-row flex flex-col items-center gap-1"
        :style="{ '--row': FOCUS_SWEEP_LAST_ROW }"
      >
        <TwnBaseBtn
          v-if="settingsPath"
          v-tooltip.right="'Settings'"
          :route-url="settingsPath"
          aria-label="Settings"
          icon="settings"
          severity="secondary"
          variant="text"
        />
        <TwnFocusSidebarTenantLinks :is-compact="true" />
        <TwnFocusSidebarUserMenu :is-compact="true" />
      </div>

      <!-- The docked panel's own space, unfolded beside the rail; switching spaces swaps it in place. -->
      <Transition name="focus-flyout">
        <section
          v-if="flyoutSection"
          :aria-label="flyoutSection.name"
          class="absolute inset-y-0 left-full z-40 flex w-[248px] flex-col gap-px border-l border-surface-200 bg-ground px-3 pb-2.5 pt-3.5 shadow-flyout-edge"
        >
          <h2 class="flex h-[32px] items-center gap-2.5 px-2 text-[13.5px] font-semibold text-surface-900">
            <MaterialIcon
              is-filled
              :ligature="flyoutSection.icon"
              class="text-selected-foreground"
              size="1rem"
            />
            <span class="truncate">{{ flyoutSection.name }}</span>
          </h2>
          <div class="min-h-0 flex-1 overflow-y-auto">
            <TwnFocusSidebarPages
              :current-path="currentPath"
              :section="flyoutSection"
              @navigate="flyoutKey = null"
            />
          </div>
          <TwnFocusSidebarLink
            is-action
            variant="space"
            @click="setMode('docked')"
          >
            <MaterialIcon
              class="text-surface-500"
              ligature="left_panel_open"
              size="1rem"
            />
            <span class="flex-1 truncate">Keep the sidebar open</span>
            <TwnKbd
              class="font-semibold"
              size="xs"
              tone="raised"
              >[</TwnKbd
            >
          </TwnFocusSidebarLink>
        </section>
      </Transition>
    </div>

    <aside
      v-else
      ref="dockedRef"
      :class="{ 'focus-entrance': isEntering, 'is-sweeping': isSweeping }"
      class="h-full w-[248px] shrink-0"
    >
      <TwnFocusSidebarPanel />
    </aside>
  </div>
</template>

<script lang="ts" setup>
import { Link, router } from '@inertiajs/vue3'
import { onClickOutside, useEventListener, usePreferredReducedMotion } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onUnmounted, ref, useTemplateRef, watch } from 'vue'

import type { MenuSection } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'

import LightLogoMark from '@/assets/svgs/light-logo-mark.svg?component'
import PinkLogoMark from '@/assets/svgs/pink-logo-mark.svg?component'
import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'
import TwnKbd from '@/components/atoms/typography/twn-kbd/TwnKbd.vue'
import { useTailwindBreakpoints } from '@/composables/use-tailwind-breakpoints/useTailwindBreakpoints'
import TwnFocusSidebarLink from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-link/TwnFocusSidebarLink.vue'
import TwnFocusSidebarPages from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-pages/TwnFocusSidebarPages.vue'
import TwnFocusSidebarPanel from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-panel/TwnFocusSidebarPanel.vue'
import TwnFocusSidebarTenantLinks from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-tenant-links/TwnFocusSidebarTenantLinks.vue'
import TwnFocusSidebarUserMenu from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-user-menu/TwnFocusSidebarUserMenu.vue'
import { FOCUS_DRAWER_DURATION } from '@/domains/admin-panel/components/organisms/menu/twn-focus-sidebar/TwnFocusSidebar.consts'
import { useCurrentPath } from '@/domains/admin-panel/composables/use-current-path/useCurrentPath'
import { useMenuStore } from '@/domains/admin-panel/store/use-menu-store/useMenuStore'
import { useSidebarPreferencesStore } from '@/domains/admin-panel/store/use-sidebar-preferences-store/useSidebarPreferencesStore'
import { FOCUS_SWEEP_LAST_ROW } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.consts'
import { firstPathOf, isSinglePageSection } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.utils'
import { useCommandPalette } from '@/domains/command-center/composables/use-command-palette/useCommandPalette'
import { TWINE_ROUTES } from '@/types/const/routes.consts'
import { isTypingTarget, MODIFIER_LABEL } from '@/utils/keyboard-util/keyboardUtil'

const homePath = TWINE_ROUTES.HOME.ROOT
const searchTooltip = `Search (${MODIFIER_LABEL}K)`

// Composables
const menuStore = useMenuStore()
const { isLargeDesktop } = useTailwindBreakpoints()
const sidebarPreferences = useSidebarPreferencesStore()
const { mode } = storeToRefs(sidebarPreferences)
const { setMode } = sidebarPreferences
const { open: openCommandPalette } = useCommandPalette()
const currentPath = useCurrentPath()
// Without the transition no `transitionend` fires, so the clip would never lift.
const prefersReducedMotion = usePreferredReducedMotion()

// State
const overlayRef = useTemplateRef<HTMLElement>('overlayRef')
const railRef = useTemplateRef<HTMLElement>('railRef')
const dockedRef = useTemplateRef<HTMLElement>('dockedRef')
// The folded rail's open space.
const flyoutKey = ref<string | null>(null)
// Rows slide in from the left on first load and every fold; only the latest sweep may end the class.
const isEntering = ref(true)
const isSweeping = ref(false)
let sweepCount = 0
// The fold animates the wrapper's width; it clips only while moving, so the rail's flyout is never cut at rest.
const isFolding = ref(false)

// Computed
const spaces = computed(() => menuStore.mainMenuMenuSections)
const settingsPath = computed(() => menuStore.serverSettingsMenuSections.map(firstPathOf).find(Boolean))
const flyoutSection = computed(() => spaces.value.find(section => section.key === flyoutKey.value))
const shellClass = computed(() => [
  mode.value === 'folded' ? 'w-[64px]' : 'w-[248px]',
  isFolding.value && 'overflow-hidden'
])

// Watchers
// Not onMounted: the breakpoint reads false until mount. Two frames first, or a busy page hides the sweep.
const stopEntranceWatch = watch(
  () => railRef.value ?? dockedRef.value,
  async sidebar => {
    if (!sidebar) return
    stopEntranceWatch()
    await nextFrame()
    await nextFrame()
    isSweeping.value = true
    await nextTick()
    endSweep(sidebar)
  },
  { flush: 'post' }
)

// The default `pre` flush runs before the swap, so the new sidebar renders already held for the sweep.
watch(mode, async next => {
  isFolding.value = prefersReducedMotion.value !== 'reduce'
  isEntering.value = true
  isSweeping.value = true
  await nextTick()

  const sidebar = next === 'folded' ? railRef.value : dockedRef.value
  if (sidebar) endSweep(sidebar)
})

// Listeners
onClickOutside(railRef, () => {
  flyoutKey.value = null
})

onClickOutside(
  overlayRef,
  () => {
    if (menuStore.isMobileMenuOpen) menuStore.closeMobileMenu()
  },
  // The header's Menu button toggles the panel itself; an outside-click close would undo it.
  { ignore: ['[data-mobile-menu-toggle]', '.p-menu'] }
)

useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    flyoutKey.value = null
    if (menuStore.isMobileMenuOpen) menuStore.closeMobileMenu()
    return
  }

  if (event.key !== '[' || event.metaKey || event.ctrlKey || event.altKey || isTypingTarget(event.target)) return
  if (!isLargeDesktop.value) return

  setMode(mode.value === 'folded' ? 'docked' : 'folded')
})

// Any real navigation closes whatever floats over the page.
const removeNavigationListener = router.on('start', () => {
  flyoutKey.value = null
  menuStore.closeMobileMenu()
})

onUnmounted(removeNavigationListener)

// Functions
const isOpen = (section: MenuSection): boolean => menuStore.currentActiveNav?.key === section.key
// The space whose panel is open, else the one the page is in.
const isRailActive = (section: MenuSection): boolean => flyoutKey.value === section.key || isOpen(section)
// A plain link opens no panel, so it states no expanded state at all.
const isFlyoutExpanded = (section: MenuSection): boolean | undefined =>
  isSinglePageSection(section) ? undefined : flyoutKey.value === section.key
// A space with no page list is itself the page, so it navigates rather than opening an empty panel.
const toggleFlyout = (section: MenuSection) => {
  const path = firstPathOf(section)
  if (isSinglePageSection(section) && path) {
    flyoutKey.value = null
    router.visit(path)
    return
  }

  flyoutKey.value = flyoutKey.value === section.key ? null : section.key
}

const isEntranceAnimation = (animation: Animation): boolean =>
  animation instanceof CSSAnimation && animation.animationName.startsWith('focus-row')
const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve))

const endSweep = async (sidebar: HTMLElement) => {
  const sweepId = ++sweepCount
  const sweep = sidebar.getAnimations({ subtree: true }).filter(isEntranceAnimation)
  await Promise.allSettled(sweep.map(animation => animation.finished))
  if (sweepId === sweepCount) isEntering.value = false
}
</script>
